import React from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import GET_LEVEL_RECOMMENDATION from "graphql/queries/GetLevelRecommendation";
import moveDown from "assets/level-recommendation/move-down.png";
import recommendationArtwork from "./LevelRecommendationArtwork";
import * as S from "./LevelRecommendationStyle";

type Props = {
  languageId: string;
  languageName: string;
  backgroundUrl: string;
};

export default function LevelRecommendation({
  languageId,
  languageName,
  backgroundUrl,
}: Props) {
  const { t, i18n } = useTranslation("book_a_class");
  const localizedArtwork = recommendationArtwork(
    i18n.resolvedLanguage || i18n.language || "en"
  );
  const { data, loading, error } = useQuery(GET_LEVEL_RECOMMENDATION, {
    variables: { languageId },
    fetchPolicy: "network-only",
  });
  const recommendation = data?.levelRecommendation;
  const level = recommendation?.currentLevel;
  const medalParts = level?.title.match(/^([^\s]{1,5})\s+(\d+(?:\.\d+)?)$/);
  const status = recommendation?.status;
  const Artwork = status === "move_down" ? S.MoveDownArtwork : S.Artwork;
  const studied = recommendation?.studiedTopics || 0;
  const total = recommendation?.totalTopics || 0;
  const percent = total
    ? Math.min(100, Math.round((studied / total) * 100))
    : 0;
  const curriculum = recommendation?.curriculumLevel;
  let artwork = localizedArtwork.stay;
  let artworkDescription = t(
    "Good progress! Keep going! You're one step closer to your goals. You can do it!"
  );
  if (status === "move_down") {
    artwork = localizedArtwork.moveDown;
    artworkDescription = t(
      "You can do it! Every great progress starts with a strong foundation. Small steps, big results!"
    );
  } else if (status === "move_up") {
    artwork = localizedArtwork.moveUp;
    artworkDescription = t(
      "Great job! You've worked hard and made amazing progress! Next level!"
    );
  }

  let message = t("Keep learning and explore your current level's curriculum.");
  if (!level)
    message = t(
      "Choose your level in Settings to see your progress and recommendations."
    );
  else if (status === "stay")
    message = t(
      "Based on your teachers' assessments, we recommend continuing {{level}} for now.",
      { level: level.title }
    );
  else if (status === "move_up")
    message = t(
      "Congrats! Based on your teachers' assessments, you're ready for the next level. Your teachers recommend moving up."
    );
  else if (status === "move_down")
    message = t(
      curriculum && curriculum.id !== level?.id
        ? "Based on your teachers' assessments, we recommend reviewing the previous level first to reinforce your knowledge and build a stronger foundation."
        : "Based on your teachers' assessments, we recommend reviewing this level to reinforce your knowledge and build a stronger foundation."
    );

  let title = t("Your current level");
  if (level) title = t("Your current level: {{level}}", { level: level.title });
  if (status === "move_down")
    title = t("{{level}} may be too challenging for you", {
      level: level?.title,
    });

  const renderContent = () => {
    if (loading) return <Skeleton active />;
    if (error)
      return (
        <S.Card>
          <p role="status">
            {t(
              "We couldn't load your level recommendation. Please try again later."
            )}
          </p>
        </S.Card>
      );
    if (status === "not_studied")
      return (
        <S.StartArtwork>
          <img
            src={localizedArtwork.start}
            alt={t(
              "Start today: learn the basics, build your foundation, and communicate with confidence."
            )}
          />
        </S.StartArtwork>
      );
    if (!recommendation) return null;
    return (
      <S.Card>
        {level &&
          (status === "move_down" ? (
            <S.MoveDownBadge role="img" aria-label={level.title}>
              <svg
                viewBox="98 185 285 249"
                aria-hidden="true"
                focusable="false"
              >
                <image href={moveDown} width="2172" height="724" />
              </svg>
              <span className="review-level" aria-hidden="true">
                {level.title}
              </span>
            </S.MoveDownBadge>
          ) : (
            <S.Badge role="img" aria-label={level.title}>
              <span className="medal-label" aria-hidden="true">
                {medalParts ? (
                  <>
                    <span className="medal-prefix">{medalParts[1]}</span>
                    <span className="medal-number">{medalParts[2]}</span>
                  </>
                ) : (
                  <span
                    className={`medal-title${
                      level.title.length > 5 ? " medal-title-long" : ""
                    }`}
                  >
                    {level.title}
                  </span>
                )}
              </span>
            </S.Badge>
          ))}
        <S.Content>
          <h2>{title}</h2>
          <p>{message}</p>
          {level && (
            <>
              <S.ProgressLabel>
                <span>{t("Topics studied")}</span>
                <span>
                  <strong>{studied}</strong> / {total} {t("topics")}
                </span>
              </S.ProgressLabel>
              <S.Progress
                role="progressbar"
                aria-label={t("Topics studied")}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
              >
                <S.ProgressFill style={{ width: `${percent}%` }} />
                <span>{percent}%</span>
              </S.Progress>
            </>
          )}
          {curriculum ? (
            <S.Curriculum to={`/my-level/${curriculum.id}`}>
              {t("View curriculum")} <span aria-hidden="true">→</span>
            </S.Curriculum>
          ) : null}
          {!level && (
            <Link to="/account/my-settings">
              {t("Choose your level in Settings")}
            </Link>
          )}
        </S.Content>
        <S.Illustration>
          <Artwork>
            <img src={artwork} alt={artworkDescription} />
            <S.Help
              href="https://flexiclasses.com/faq-topic/booking-classes/"
              target="_blank"
              rel="noreferrer"
              aria-label={t("Help with level recommendations")}
            />
          </Artwork>
        </S.Illustration>
      </S.Card>
    );
  };

  return (
    <S.Section
      style={{
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
      }}
      aria-label={t("Level recommendation")}
    >
      <S.Language>{t(languageName.trim())}</S.Language>
      {renderContent()}
    </S.Section>
  );
}
